// -- [Rolodex / index-cards style animated value; only changed digits burn in from orange to white]

import { useState, useEffect, useRef, useMemo } from 'react'

const ROLODEX_DURATION_MS = 500
const BURN_IN_DURATION_MS = 1500

export interface AnimatedStatValueProps {
  /** Formatted string to display (e.g. "1,234,567" or "$40,892.12") */
  formatted: string
  /** Optional className for the wrapper */
  className?: string
  /** If true, allow value to wrap to multiple lines and don't clip (no overflow-hidden) */
  wrap?: boolean
}

function getChangedIndices(current: string, previous: string): Set<number> {
  const len = Math.max(current.length, previous.length)
  const curPadded = current.padStart(len, ' ')
  const prevPadded = previous.padStart(len, ' ')
  const set = new Set<number>()
  for (let i = 0; i < len; i++) {
    if (curPadded[i] !== prevPadded[i]) set.add(i)
  }
  // -- Return indices relative to current string length so we can index into current
  if (current.length >= previous.length) return set
  const adjusted = new Set<number>()
  const offset = len - current.length
  set.forEach((i) => {
    if (i >= offset) adjusted.add(i - offset)
  })
  return adjusted
}

export function AnimatedStatValue({ formatted, className, wrap }: AnimatedStatValueProps) {
  const [displayed, setDisplayed] = useState(formatted)
  const [previous, setPrevious] = useState(formatted)
  const [previousForBurnIn, setPreviousForBurnIn] = useState<string | null>(null)
  const [animating, setAnimating] = useState(false)
  const [slideToEnd, setSlideToEnd] = useState(false)
  const [burnInActive, setBurnInActive] = useState(false)
  const burnInTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (formatted === displayed) return

    if (burnInTimeoutRef.current) {
      clearTimeout(burnInTimeoutRef.current)
      burnInTimeoutRef.current = null
    }
    setBurnInActive(false)
    setPreviousForBurnIn(null)

    if (wrap) {
      // -- When wrapping, skip rolodex slide so we don't show two stacked values; update in place and burn-in
      setPreviousForBurnIn(displayed)
      setDisplayed(formatted)
      setBurnInActive(true)
      burnInTimeoutRef.current = setTimeout(() => {
        burnInTimeoutRef.current = null
        setBurnInActive(false)
        setPreviousForBurnIn(null)
      }, BURN_IN_DURATION_MS)
      return
    }

    setSlideToEnd(false)
    setPrevious(displayed)
    setAnimating(true)
    const raf = requestAnimationFrame(() => {
      requestAnimationFrame(() => setSlideToEnd(true))
    })
    return () => cancelAnimationFrame(raf)
  }, [formatted, displayed, wrap])

  const handleTransitionEnd = (e: React.TransitionEvent) => {
    if (e.propertyName !== 'transform') return
    setPreviousForBurnIn(previous)
    setDisplayed(formatted)
    setAnimating(false)
    setSlideToEnd(false)
    setBurnInActive(true)
    if (burnInTimeoutRef.current) clearTimeout(burnInTimeoutRef.current)
    burnInTimeoutRef.current = setTimeout(() => {
      burnInTimeoutRef.current = null
      setBurnInActive(false)
      setPreviousForBurnIn(null)
    }, BURN_IN_DURATION_MS)
  }

  useEffect(() => {
    return () => {
      if (burnInTimeoutRef.current) clearTimeout(burnInTimeoutRef.current)
    }
  }, [])

  const changedIndices = useMemo(() => {
    if (!burnInActive || previousForBurnIn == null) return new Set<number>()
    return getChangedIndices(displayed, previousForBurnIn)
  }, [burnInActive, previousForBurnIn, displayed])

  if (!animating) {
    const chars = displayed.split('')
    const wrapClasses = wrap
      ? 'flex-wrap overflow-visible max-w-full'
      : 'overflow-hidden h-[1.2em]'
    return (
      <span className={`inline-flex align-top tabular-nums ${wrapClasses} ${className ?? ''}`}>
        {chars.map((char, i) => {
          const isChanged = changedIndices.has(i)
          // -- Yellow on blue is decorative flash; base stays white for AA contrast
          const textClass = isChanged ? 'text-white bg-white/20 rounded-sm' : 'text-white'
          // -- Use non-breaking space so spaces don't collapse in flex layout
          const content = char === ' ' ? '\u00A0' : char
          return (
            <span
              key={`${i}-${char}`}
              className={`inline-flex items-center justify-center transition-colors duration-[1500ms] ${textClass}`}
            >
              {content}
            </span>
          )
        })}
      </span>
    )
  }

  return (
    <span
      className={`inline-block align-top tabular-nums ${wrap ? 'overflow-visible max-w-full' : 'h-[1.2em] w-full overflow-hidden'} ${className ?? ''}`}
    >
      <span
        className="block w-full transition-transform ease-out"
        style={{
          height: '2.4em',
          transform: slideToEnd ? 'translateY(-50%)' : 'translateY(0)',
          transitionDuration: `${ROLODEX_DURATION_MS}ms`,
        }}
        onTransitionEnd={handleTransitionEnd}
      >
        <div className="flex h-[1.2em] min-h-[1.2em] items-center justify-center tabular-nums text-white">
          {previous}
        </div>
        <div className="flex h-[1.2em] min-h-[1.2em] items-center justify-center tabular-nums text-white">
          {formatted}
        </div>
      </span>
    </span>
  )
}
